"use client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Link from "next/link";

import { useFeedback } from "@/components/dashboard/feedback-component";
import { IconBook, IconBraces, IconChats, IconCode, IconDiscord, IconHelp } from "@teral/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@teral/ui";
import { useState } from "react";
import { TOP_NAV_ICON_BUTTON_CLASS } from "./icon-button";
import { StatusWidget } from "./status-widget";

const ITEM_LINKS = [
  { href: "https://docs.teral.dev/", label: "Docs", icon: IconBook },
  {
    href: "https://docs.teral.dev/api-reference/overview",
    label: "API Reference",
    icon: IconBraces,
  },
  { href: "https://teral.dev/discord", label: "Community", icon: IconDiscord },
] as const;

// AGPLv3 section 13: users of the dashboard must be offered the source of the
// version they use, which tools/publish-source.sh uploads. Keep it in this
// menu, which every page has, and shown to every user: never put it behind a
// flag.
// The anchor highlights this deployment's archive in the list. The build
// script sets the commit (package.json); scripts/require-source.mjs makes sure
// the archive exists.
const COMMIT = process.env.NEXT_PUBLIC_SOURCE_COMMIT;
const LICENSES_LINK = {
  href: `https://teral-source.s3.eu-central-1.amazonaws.com/index.html${COMMIT ? `#teral-${COMMIT.slice(0, 12)}` : ""}`,
  label: "Licenses",
  icon: IconCode,
} as const;

export function HelpButton({ variant = "inline" }: { variant?: "inline" | "rail" }) {
  const rail = variant === "rail";
  const { openFeedback } = useFeedback();
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const links = [...ITEM_LINKS, LICENSES_LINK];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <Tooltip open={tooltipOpen && !open} onOpenChange={setTooltipOpen}>
        <DropdownMenuTrigger
          aria-label="Help options"
          render={<TooltipTrigger />}
          className={TOP_NAV_ICON_BUTTON_CLASS}
        >
          <IconHelp className="size-3.5" />
        </DropdownMenuTrigger>
        <TooltipContent side={rail ? "top" : "bottom"}>Help</TooltipContent>
      </Tooltip>
      <DropdownMenuContent side={rail ? "right" : "bottom"} align="end" className="w-56 p-0">
        <DropdownMenuGroup className="p-1">
          {links.map(({ href, label, icon: Icon }) => (
            <DropdownMenuItem
              key={href}
              className="h-8 cursor-pointer gap-2 px-2 text-sm font-medium text-gray-12"
              render={
                <Link href={href} target="_blank" rel="noreferrer">
                  <Icon className="size-4 shrink-0 text-gray-11" />
                  {label}
                </Link>
              }
            />
          ))}
          <DropdownMenuItem
            className="h-8 cursor-pointer gap-2 px-2 text-sm font-medium text-gray-12"
            onClick={() => {
              setOpen(false);
              openFeedback(true);
            }}
          >
            <IconChats className="size-4 shrink-0 text-gray-11" />
            Support
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator className="mx-0" />
        <DropdownMenuGroup className="p-1">
          <DropdownMenuLabel className="px-2">Platform status</DropdownMenuLabel>
          <DropdownMenuItem
            className="h-8 cursor-pointer px-2"
            render={<StatusWidget className="gap-2 text-sm" />}
          />
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
